import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getTitleById, getRelated } from '@/utils/titles';
import { useFavorites } from '@/hooks/useFavorites';
import Breadcrumb from '@/components/base/Breadcrumb';
import RatingBadge from '@/components/base/RatingBadge';
import TitleCard from '@/components/feature/TitleCard';
import SectionHeading from '@/components/feature/SectionHeading';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function DetailPage() {
  const { id = '' } = useParams();
  const item = useMemo(() => getTitleById(id), [id]);
  const related = useMemo(() => (item ? getRelated(item, 6) : []), [item]);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [activeEpisode, setActiveEpisode] = useState(1);
  const [toast, setToast] = useState('');

  usePageMeta({
    title: item
      ? `${item.type === 'movie' ? '电影' : '电视剧'}《${item.title}》详情 - 秋霞电影网`
      : '影视详情 - 秋霞电影网',
    description: item
      ? `《${item.title}》${item.year}年${item.region}${item.type === 'movie' ? '电影' : '电视剧'}，由${item.director}执导，${item.cast.slice(0, 3).join('、')}主演。${item.synopsis}`
      : '影视内容详情页',
    keywords: item
      ? `${item.title},${item.title}在线观看,${item.genres.join(',')},${item.region}${item.type === 'movie' ? '电影' : '电视剧'},秋霞电影网`
      : '秋霞电影网',
  });

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-4">
          <i className="ri-movie-line"></i>
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">未找到该影视内容</h1>
        <p className="text-foreground-600 mb-6">它可能已被移除，或链接有误。</p>
        <Link
          to="/"
          className="h-10 px-5 flex items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(item.id);
  const episodes = item.type === 'tv' && item.episodes ? Array.from({ length: item.episodes }, (_, i) => i + 1) : [];
  const categoryLabel = item.type === 'movie' ? '电影' : '电视剧';
  const categoryTo = item.type === 'movie' ? '/category/movie' : '/category/tv';

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2000);
  };

  const handleShare = () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => showToast('链接已复制，快去分享吧'),
        () => showToast('分享链接：' + url),
      );
    } else {
      showToast('分享链接：' + url);
    }
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: categoryLabel, to: categoryTo }, { label: item.title }]} />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-8">
        <div className="mx-auto lg:mx-0 w-48 sm:w-56 lg:w-full">
          <div className="w-full aspect-[2/3] rounded-xl overflow-hidden bg-background-200 border border-background-200">
            <img src={item.poster} alt={`${item.title}海报`} title={`${item.title}在线观看`} className="w-full h-full object-cover object-top" />
          </div>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">{item.title}</h1>

          <div className="flex flex-wrap items-center gap-3 mt-3">
            <span className="inline-flex items-center gap-2">
              <RatingBadge rating={item.rating} />
            </span>
            <span className={`rounded-md px-2 py-1 text-xs ${item.type === 'movie' ? 'bg-secondary-100 text-secondary-900' : 'bg-primary-100 text-primary-800'}`}>
              {categoryLabel}
            </span>
            {item.type === 'tv' && item.status && (
              <span className={`rounded-md px-2 py-1 text-xs ${item.status === 'ongoing' ? 'bg-accent-100 text-accent-800' : 'bg-background-200 text-foreground-700'}`}>
                {item.status === 'ongoing' ? `连载至第 ${item.episodes} 集` : '已完结'}
              </span>
            )}
          </div>

          <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div className="flex gap-2">
              <dt className="text-foreground-500 shrink-0 w-16">导演</dt>
              <dd className="text-foreground-900">{item.director}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-foreground-500 shrink-0 w-16">地区</dt>
              <dd className="text-foreground-900">{item.region}</dd>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <dt className="text-foreground-500 shrink-0 w-16">主演</dt>
              <dd className="text-foreground-900">{item.cast.join('、')}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-foreground-500 shrink-0 w-16">类型</dt>
              <dd className="text-foreground-900">{item.genres.join(' / ')}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-foreground-500 shrink-0 w-16">年份</dt>
              <dd className="text-foreground-900">{item.year}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-foreground-500 shrink-0 w-16">{item.type === 'movie' ? '片长' : '集数'}</dt>
              <dd className="text-foreground-900">{item.type === 'movie' ? item.duration : `全 ${item.episodes} 集`}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-foreground-500 shrink-0 w-16">更新</dt>
              <dd className="text-foreground-900">{item.updatedAt}</dd>
            </div>
          </dl>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                toggleFavorite(item.id);
                showToast(favorited ? '已取消收藏' : '已加入收藏');
              }}
              className={`h-11 px-5 inline-flex items-center gap-2 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                favorited
                  ? 'bg-accent-500 text-primary-950 hover:bg-accent-600'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              <i className={favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
              {favorited ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="h-11 px-5 inline-flex items-center gap-2 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line"></i>分享
            </button>
            <span className="h-11 px-5 inline-flex items-center gap-2 rounded-md bg-background-100 text-foreground-500 text-sm">
              <i className="ri-information-line"></i>演示环境 · 暂不支持真实播放
            </span>
          </div>
        </div>
      </div>

      <section className="mt-9">
        <SectionHeading title="剧情简介" />
        <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-6">
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{item.synopsis}</p>
          <p className="mt-3 text-sm text-foreground-600 leading-loose">
            《{item.title}》是一部 {item.year} 年出品的{item.region}{categoryLabel}，由 {item.director} 执导，
            {item.cast.slice(0, 3).join('、')} 领衔主演。影片融合 {item.genres.join('、')} 等元素，为观众呈现一段引人入胜的故事。
          </p>
        </div>
      </section>

      {item.type === 'tv' && episodes.length > 0 && (
        <section className="mt-9">
          <SectionHeading title="剧集目录" subtitle="点击选集查看单集简介（演示）" />
          <div className="flex gap-2 overflow-x-auto md:flex-wrap md:overflow-visible pb-2 no-scrollbar">
            {episodes.map((ep) => (
              <button
                key={ep}
                type="button"
                onClick={() => setActiveEpisode(ep)}
                className={`shrink-0 w-12 h-11 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  activeEpisode === ep
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {ep}
              </button>
            ))}
          </div>

          <div className="mt-4 rounded-lg bg-background-100 border border-background-200 p-4 md:p-6 flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-40 h-24 shrink-0 rounded-md bg-primary-950/90 flex flex-col items-center justify-center text-background-200">
              <i className="ri-play-circle-line text-3xl text-accent-400"></i>
              <span className="text-xs mt-1">预览占位</span>
            </div>
            <div className="min-w-0">
              <h3 className="font-heading font-semibold text-foreground-950">
                第 {activeEpisode} 集
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                《{item.title}》第 {activeEpisode} 集：随着剧情推进，主角面临新的抉择，隐藏的线索逐渐浮出水面。
                （此集数信息为前端演示内容，不包含真实视频播放。）
              </p>
            </div>
          </div>
        </section>
      )}

      {item.type === 'movie' && (
        <section className="mt-9">
          <SectionHeading title="播放入口（演示）" />
          <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="w-full sm:w-52 h-28 shrink-0 rounded-md bg-primary-950/90 flex flex-col items-center justify-center text-background-200">
              <i className="ri-play-circle-line text-4xl text-accent-400"></i>
              <span className="text-xs mt-1">预览占位</span>
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground-950">片长约 {item.duration}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                本站为演示环境，仅展示影视信息与片长，不提供真实视频播放或下载功能。
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="mt-9">
        <SectionHeading title="相关推荐" subtitle="与当前影视相似的热门内容" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {related.map((rel) => (
            <TitleCard key={rel.id} item={rel} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-in-up">
          {toast}
        </div>
      )}
    </div>
  );
}